import 'react-native-gesture-handler';
import React, { useEffect } from 'react';
import { Stack } from 'expo-router';
import Head from 'expo-router/head';
import { StatusBar } from 'expo-status-bar';
import { View, Text, Platform, ImageBackground, StyleSheet, ActivityIndicator } from 'react-native';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { RachaProvider } from '@/contexts/RachaContext';
import { GruposProvider } from '@/contexts/GruposContext';
import { CategoriaProvider } from '@/contexts/CategoriaContext';
import { ErrorBoundary } from 'react-error-boundary';
import { useFonts, Inter_400Regular, Inter_500Medium, Inter_600SemiBold, Inter_700Bold } from '@expo-google-fonts/inter';
import * as SplashScreen from 'expo-splash-screen';
import { logger, replaceConsoleInProduction } from '../utils/logger';
import { PWAInstallPrompt } from '@/components/PWAInstallPrompt';

// ... (keep existing imports)

// ...


function LoadingScreen() {
  return (
    <ImageBackground
      source={Platform.OS === 'web' ? require('@/assets/images/pwa-splash.webp') : require('@/assets/images/pwa-splash.png')}
      style={styles.background}
      resizeMode="cover"
    >
      <ActivityIndicator size="large" color="#ffffff" />
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  background: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#000000'
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#1a1a1a',
  },
  errorTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ff6b6b',
    marginBottom: 16,
  },
  errorMessage: {
    fontSize: 16,
    color: '#f0f0f0',
    textAlign: 'center',
    lineHeight: 24,
  },
});

// Componente de fallback para erros
function ErrorFallback({ error }: { error: Error }) {
  return (
    <View style={styles.errorContainer}>
      <Text style={styles.errorTitle}>Oops! Algo deu errado.</Text>
      <Text style={styles.errorMessage}>
        Ocorreu um erro inesperado que impediu o aplicativo de iniciar corretamente.
        Por favor, tente reiniciar o aplicativo.
      </Text>
      {__DEV__ && ( // Mostra detalhes do erro apenas em modo de desenvolvimento
        <Text style={[styles.errorMessage, { marginTop: 20, color: '#ffb8b8' }]}>
          Detalhes do Erro: {error.message}
        </Text>
      )}
    </View>
  );
}

function applyInterFontDisplaySwap() {
  if (typeof document === 'undefined') return;
  if (typeof CSSRule === 'undefined') return;

  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const rules = sheet.cssRules;
      for (const rule of Array.from(rules)) {
        if (rule.type !== CSSRule.FONT_FACE_RULE) continue;
        const fontRule = rule as CSSFontFaceRule;
        const family = fontRule.style.getPropertyValue('font-family');
        if (!family) continue;
        if (!family.toLowerCase().includes('inter')) continue;
        fontRule.style.setProperty('font-display', 'swap');
      }
    } catch {
      continue;
    }
  }
}

// Componente interno que consome os contextos
function RootLayoutInner() {
  const { loading } = useAuth();
  const [fontsLoaded, fontError] = useFonts({
    'Inter-Regular': Inter_400Regular,
    'Inter-Medium': Inter_500Medium,
    'Inter-SemiBold': Inter_600SemiBold,
    'Inter-Bold': Inter_700Bold,
  });

  useEffect(() => {
    // Inicializar sistema de logging seguro
    replaceConsoleInProduction();

    if (fontsLoaded || fontError) {
      SplashScreen.hideAsync();
    }
    if (fontError) {
      logger.warn('Error loading fonts', { error: fontError });
    }
    if (Platform.OS === 'web') {
      applyInterFontDisplaySwap();
      const existing = document.getElementById('adsbygoogle-init');
      if (!existing) {
        const s = document.createElement('script');
        s.id = 'adsbygoogle-init';
        s.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-2511123537845418';
        s.crossOrigin = 'anonymous';
        s.async = true;
        document.head.appendChild(s);
      }
    }
  }, [fontsLoaded, fontError]);

  return (
    <>
      <Head>
        <title>Põe no Racha | Divisão de Contas Simples</title>
        <meta
          name="description"
          content="A melhor forma de dividir despesas de viagens, repúblicas e churrascos com amigos. Gere cobranças via Pix e evite planilhas."
        />
      </Head>
      <GruposProvider>
        <RachaProvider>
          <CategoriaProvider>
            {loading ? (
              <LoadingScreen />
            ) : (
              <Stack screenOptions={{ headerShown: false }}>
                <Stack.Screen name="index" options={{ headerShown: false }} />
                <Stack.Screen name="(auth)" options={{ headerShown: false }} />
                <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
                <Stack.Screen name="+not-found" />
              </Stack>
            )}
            <StatusBar style="auto" />
            <PWAInstallPrompt />
          </CategoriaProvider>
        </RachaProvider>
      </GruposProvider>
    </>
  );
}

// Componente principal com Error Boundary
export default function RootLayout() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Head.Provider>
        <ThemeProvider>
          <AuthProvider>
            <RootLayoutInner />
          </AuthProvider>
        </ThemeProvider>
      </Head.Provider>
    </ErrorBoundary>
  );
}

